import DOMPurify from "dompurify"
import mermaid, { type MermaidConfig } from "mermaid"

import { renderMermaidExclusive } from "@/lib/mermaid-render-lock"

/**
 * Render Mermaid code to SVG markup that is safe to put in the app DOM or a file.
 *
 * The editor previews, the visual editor's layout pass, the editor's SVG/PNG
 * download and the chat Word export all render code that can come from another
 * user or a chat answer. They render with securityLevel "strict", so Mermaid
 * encodes HTML in labels and limits click directives:
 * - `click … call fn()` callbacks never run.
 * - `click … href` keeps http(s) links but drops `javascript:` and other unsafe
 *   URLs, and ignores a `_blank` target.
 * The directives stay in the code, so saving keeps them.
 *
 * Mermaid's config is global and read when a queued render starts, so one caller
 * that initializes "loose" would apply to every render queued behind it. No caller
 * in the app does (mermaid-security-level.test.ts enforces that). The DOMPurify
 * pass, with the options Mermaid uses for "strict", keeps the returned SVG safe
 * even if that changes. Font and theme still differ per caller, so the render
 * goes through renderMermaidExclusive and runs under exactly this config.
 */
export async function renderSanitizedMermaidSvg(
  id: string,
  code: string,
  config: MermaidConfig = {}
): Promise<string> {
  const { svg } = await renderMermaidExclusive(mermaid, {
    config: { ...config, startOnLoad: false, securityLevel: "strict" },
    id,
    code: code.trim(),
  })
  return DOMPurify.sanitize(svg, {
    ADD_TAGS: ["foreignobject"],
    ADD_ATTR: ["dominant-baseline"],
    HTML_INTEGRATION_POINTS: { foreignobject: true },
  })
}
